import React, { createContext, useState, useCallback, useEffect } from 'react';
import { apiService, configureApiService } from '../services/apiService';
import { SettingItem, Currency, SafeUser, USER_COLOR_HISTORY_PREF_KEY, USER_PER_ITEM_COLOR_HISTORY_PREF_KEY, LOG_LEVEL_PREF_KEY } from '../types';
import { DEFAULT_COLORS } from '../constants';
import { logger, LogLevel } from '../services/logger';

type SettingType = 'tags' | 'categories' | 'paymentMethods';
export type ManageableType = SettingType | 'currencies';
type PerItemColorHistory = Partial<Record<ManageableType, Record<number, string[]>>>;

export interface BusinessItem {
    name: string;
    count: number;
}

const STORE_NAMES: Record<ManageableType, string> = {
    tags: 'tags',
    categories: 'categories',
    paymentMethods: 'paymentMethods',
    currencies: 'currencies',
};

export interface SettingsContextType {
    tags: SettingItem[];
    categories: SettingItem[];
    paymentMethods: SettingItem[];
    currencies: Currency[];
    businessNames: BusinessItem[];
    users: SafeUser[];
    colorHistory: string[];
    perItemColorHistory: PerItemColorHistory;
    isLoading: boolean;
    addItem(type: 'currencies', newItemData: { name: string; color?: undefined }): Promise<Currency>;
    addItem(type: SettingType, newItemData: { name: string; color?: string }): Promise<SettingItem>;
    addItem(type: ManageableType, newItemData: { name: string; color?: string }): Promise<SettingItem | Currency>;
    updateItem: (type: ManageableType, updatedItem: SettingItem | Currency) => Promise<void>;
    saveAllItems: (type: ManageableType, itemsToSave: (SettingItem | Currency)[]) => Promise<void>;
    deleteItem: (type: ManageableType, itemId: number) => Promise<void>;
    mergeItems: (type: ManageableType, sourceId: number, targetId: number) => Promise<void>;
    mergeBusiness: (sourceName: string, targetName: string) => Promise<void>;
    addBusiness: (name: string) => Promise<void>;
    updateBusinessName: (oldName: string, newName: string) => Promise<void>;
    addUser: (username: string, password: string) => Promise<SafeUser | false>;
    updateUser: (userId: number, updates: { username?: string; newPassword?: string }) => Promise<{ success: boolean; error?: string }>;
    deleteUser: (userId: number) => Promise<void>;
    addColorToHistory: (newColor: string) => Promise<void>;
    addColorToItemHistory: (type: ManageableType, itemId: number, oldColor: string) => Promise<void>;
    refresh: () => Promise<void>;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [tags, setTags] = useState<SettingItem[]>([]);
    const [categories, setCategories] = useState<SettingItem[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<SettingItem[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [businessNames, setBusinessNames] = useState<BusinessItem[]>([]);
    const [users, setUsers] = useState<SafeUser[]>([]);
    const [colorHistory, setColorHistory] = useState<string[]>([]);
    const [perItemColorHistory, setPerItemColorHistory] = useState<PerItemColorHistory>({});
    const [isLoading, setIsLoading] = useState(true);
    
    const stateMap = {
        tags: { list: tags, setter: setTags },
        categories: { list: categories, setter: setCategories },
        paymentMethods: { list: paymentMethods, setter: setPaymentMethods },
        currencies: { list: currencies, setter: setCurrencies },
    };

    const fetchAllSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            await configureApiService();
            
            const [tagsData, categoriesData, paymentMethodsData, currenciesData, businessNamesData, usersData, colorHistoryData, perItemHistoryData, logLevelData] = await Promise.all([
                apiService.getAll<SettingItem>(STORE_NAMES.tags),
                apiService.getAll<SettingItem>(STORE_NAMES.categories),
                apiService.getAll<SettingItem>(STORE_NAMES.paymentMethods),
                apiService.getAll<Currency>(STORE_NAMES.currencies),
                apiService.getUniqueBusinessNames(),
                apiService.getAllUsers(),
                apiService.getPreference<string[]>(USER_COLOR_HISTORY_PREF_KEY, []),
                apiService.getPreference<PerItemColorHistory>(USER_PER_ITEM_COLOR_HISTORY_PREF_KEY, {}),
                apiService.getPreference<LogLevel>(LOG_LEVEL_PREF_KEY, LogLevel.INFO),
            ]);
            setTags(tagsData.sort((a, b) => a.name.localeCompare(b.name, 'cs')));
            setCategories(categoriesData.sort((a, b) => a.name.localeCompare(b.name, 'cs')));
            setPaymentMethods(paymentMethodsData.sort((a, b) => a.name.localeCompare(b.name, 'cs')));
            setCurrencies(currenciesData.sort((a, b) => a.name.localeCompare(b.name, 'cs')));
            setBusinessNames(businessNamesData);
            setUsers(usersData.sort((a, b) => a.username.localeCompare(b.username, 'cs')));
            setColorHistory(colorHistoryData);
            setPerItemColorHistory(perItemHistoryData);
            logger.setLevel(logLevelData);

        } catch (error) {
            console.error("Failed to load settings:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllSettings();
    }, [fetchAllSettings]);
    
    const getNextColor = (type: SettingType) => {
         const list = stateMap[type].list;
         return DEFAULT_COLORS[list.length % DEFAULT_COLORS.length];
    }

    const addColorToHistory = useCallback(async (newColor: string) => {
        setColorHistory(prev => {
            const newHistory = [newColor, ...prev.filter(c => c.toLowerCase() !== newColor.toLowerCase())].slice(0, 16);
            apiService.setPreference(USER_COLOR_HISTORY_PREF_KEY, newHistory);
            return newHistory;
        });
    }, []);

    const addColorToItemHistory = useCallback(async (type: ManageableType, itemId: number, oldColor: string) => {
        // FIX: Removed comparison of `type` to 'businesses' as it is not part of ManageableType, resolving a TypeScript error.
        if (!type || !itemId || !oldColor || type === 'currencies') return;

        setPerItemColorHistory(prev => {
            const newHistory: PerItemColorHistory = JSON.parse(JSON.stringify(prev));

            if (!newHistory[type]) {
                newHistory[type] = {};
            }
            const typeHistory = newHistory[type]!;

            if (!typeHistory[itemId]) {
                typeHistory[itemId] = [];
            }
            
            const currentItemHistory = typeHistory[itemId];
            
            const updatedItemHistory = [oldColor, ...currentItemHistory.filter(c => c.toLowerCase() !== oldColor.toLowerCase())].slice(0, 8);
            
            typeHistory[itemId] = updatedItemHistory;

            apiService.setPreference(USER_PER_ITEM_COLOR_HISTORY_PREF_KEY, newHistory);
            return newHistory;
        });
    }, []);

    const addItem = (async (type: ManageableType, newItemData: { name: string, color?: string }): Promise<SettingItem | Currency> => {
        const { setter } = stateMap[type];
        const storeName = STORE_NAMES[type];
        
        let itemToCreate: Omit<SettingItem, 'id'> | Omit<Currency, 'id'>;

        if (type === 'currencies') {
            itemToCreate = { name: newItemData.name };
        } else {
            itemToCreate = {
                name: newItemData.name,
                color: newItemData.color || getNextColor(type as SettingType),
            };
        }
        
        const createdItem = await apiService.findOrCreate(storeName, itemToCreate);
        (setter as React.Dispatch<React.SetStateAction<(SettingItem|Currency)[]>>)(prev => [...prev, createdItem].sort((a, b) => a.name.localeCompare(b.name, 'cs')));
        return createdItem;
    }) as SettingsContextType['addItem'];

    const updateItem = async (type: ManageableType, updatedItem: SettingItem | Currency) => {
        const { setter } = stateMap[type];
        const storeName = STORE_NAMES[type];

        await apiService.saveAll(storeName, [updatedItem]);
        
        setter(prev => 
            prev.map(i => (i.id === updatedItem.id ? updatedItem : i))
                .sort((a, b) => a.name.localeCompare(b.name, 'cs')) as any
        );
    };

    const saveAllItems = async (type: ManageableType, itemsToSave: (SettingItem | Currency)[]) => {
        const { setter } = stateMap[type];
        const storeName = STORE_NAMES[type];
    
        await apiService.saveAll(storeName, itemsToSave);
    
        setter(itemsToSave.sort((a, b) => a.name.localeCompare(b.name, 'cs')) as any);
    };
    
    const deleteItem = async (type: ManageableType, itemId: number) => {
        const { setter } = stateMap[type];
        const storeName = STORE_NAMES[type];
        
        await apiService.deleteItem(storeName, itemId);
        
        setter(prev => prev.filter(i => i.id !== itemId));
    };
    
    const mergeItems = async (type: ManageableType, sourceId: number, targetId: number) => {
        const storeName = STORE_NAMES[type];
        if (!storeName) return;

        await apiService.mergeSettingItems(storeName, sourceId, targetId);
        await fetchAllSettings();
    };

    const mergeBusiness = async (sourceName: string, targetName: string) => {
        await apiService.mergeBusiness(sourceName, targetName);
        await fetchAllSettings();
    };

    const addBusiness = async (name: string) => {
        try {
            await apiService.addBusiness(name);
            await fetchAllSettings();
        } catch (error) {
            console.error("Failed to add business:", error);
            throw error;
        }
    };

    const updateBusinessName = async (oldName: string, newName: string) => {
        await apiService.updateBusinessName(oldName, newName);
        await fetchAllSettings();
    };

    const addUser = async (username: string, password: string): Promise<SafeUser | false> => {
        try {
            const newUser = await apiService.addUser(username, password);
            await fetchAllSettings();
            return newUser;
        } catch (e) {
            console.error("Failed to add user:", e);
            return false;
        }
    };

    const updateUser = async (userId: number, updates: { username?: string; newPassword?: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            await apiService.updateUser(userId, updates);
            await fetchAllSettings();
            return { success: true };
        } catch (e: any) {
            console.error("Failed to update user:", e);
            return { success: false, error: e.message };
        }
    };

    const deleteUser = async (userId: number) => {
        await apiService.deleteUser(userId);
        await fetchAllSettings();
    };


    const value: SettingsContextType = {
        tags,
        categories,
        paymentMethods,
        currencies,
        businessNames,
        users,
        colorHistory,
        perItemColorHistory,
        isLoading,
        addItem,
        updateItem,
        saveAllItems,
        deleteItem,
        mergeItems,
        mergeBusiness,
        addBusiness,
        updateBusinessName,
        addUser,
        updateUser,
        deleteUser,
        addColorToHistory,
        addColorToItemHistory,
        refresh: fetchAllSettings,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};